import { FontAwesomeIcon } from 'components/shared/icons/FontAwesomeIcon/FontAwesomeIcon';
import { Tooltip } from 'components/shared/messages/Tooltip/Tooltip';
import React, { useRef, useState, MouseEvent, useEffect } from 'react';

import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { isFunction } from 'lodash';
import classNames from 'classnames';

import { useTranslator } from 'components/shared/hooks';
import { BaseActionButtonProps, SingleAction } from 'components/shared/buttons/ActionButton/ActionButton.types';
import { useStyles } from 'components/shared/buttons/ActionButton/ActionButton.styles';

export const BaseActionButtonComponent = (props: BaseActionButtonProps, ref) => {
  const {
    label,
    actionList,
    startingIndex = -1,
    component,
    collectNode,
    disableCaret,
    componentActions,
    wrapperClassName,
    className,
    ...buttonProps
  } = props;

  const trans = useTranslator();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const classes = useStyles({ hasCaret: !disableCaret });
  const ButtonComponent = component || Button;

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleAction = (event: MouseEvent, action: () => void) => {
    event.stopPropagation();
    action();
    handleClose();
  };

  useEffect(() => {
    componentActions &&
      componentActions({
        close: handleClose,
      });
  }, [componentActions]);

  const renderAction = (action: SingleAction, index: number) => {
    return (
      <Button
        key={index}
        onClick={(event) => handleAction(event, action.action)}
        ref={(node) => props.collectNode(node as HTMLElement, index + startingIndex + 1)}
        className={classes.menuButton}
        {...action.props}
      >
        {action.icon && <FontAwesomeIcon name={action.icon} className={classNames(classes.menuButtonIcon, 'mr-2')} />}
        {trans(action.label)}
      </Button>
    );
  };

  const renderActionWithTooltip = (action: SingleAction, index: number) => {
    return (
      <Tooltip key={index} {...action.tooltip}>
        {renderAction(action, index)}
      </Tooltip>
    );
  };

  return (
    <span ref={ref} className={wrapperClassName}>
      <ButtonComponent
        aria-owns={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        ref={(node: HTMLButtonElement) => {
          anchorRef.current = node;
          collectNode(node as HTMLElement, startingIndex);
        }}
        className={classNames(classes.button, className)}
        {...buttonProps}
      >
        {isFunction(label) ? label(trans) : trans(label)}
        {!disableCaret && <ArrowDropDownIcon className={classes.caret} />}
      </ButtonComponent>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: false,
            options: {
              boundary: 'scrollParent',
            },
          },
        ]}
        className={classes.popper}
      >
        <Paper id="menu-list-grow">
          <ClickAwayListener onClickAway={handleClose}>
            <div className={classes.menuList}>
              {actionList.map((action, index) => {
                return action.tooltip ? renderActionWithTooltip(action, index) : renderAction(action, index);
              })}
            </div>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </span>
  );
};

BaseActionButtonComponent.displayName = 'BaseActionButtonComponent';
export const BaseActionButton = React.forwardRef(BaseActionButtonComponent);
